import { format, formatDistanceToNow, parseISO, startOfWeek } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/**
 * Datas no fuso da pessoa, nunca em UTC.
 *
 * `new Date().toISOString().slice(0, 10)` é o dia em Greenwich: a partir das
 * 21h em Brasília, "hoje" já é amanhã. Prazo virava "atrasada" cedo, o mês de
 * fechamento mudava no último dia às 21h, a ocorrência de rotina caía no dia
 * seguinte (relatório §0 problema 12). Tudo o que precisa de "hoje", "este
 * mês" ou "esta semana" passa por aqui.
 *
 * Datas de calendário (`yyyy-MM-dd`) continuam sendo comparadas como string —
 * é o que `dueState` em `@sistemas/contracts` espera.
 */

/** `Date` local → `yyyy-MM-dd`. */
export const toLocalIso = (date: Date): string => format(date, 'yyyy-MM-dd')

/** `Date` local → `yyyy-MM`. */
export const toLocalMonth = (date: Date): string => format(date, 'yyyy-MM')

export const todayLocalIso = (): string => toLocalIso(new Date())

export const currentMonthLocal = (): string => toLocalMonth(new Date())

/**
 * Interpreta `yyyy-MM-dd` como data local (não UTC, como `new Date(str)` faz)
 * e timestamps completos como instantes.
 */
export function parseLocal(iso: string): Date {
  return parseISO(iso)
}

/** `dd/MM/yyyy` — padrão do doc 18 §7. */
export function formatDate(iso: string): string {
  return format(parseLocal(iso), 'dd/MM/yyyy')
}

/** `dd/MM/yyyy HH:mm`, 24h. */
export function formatDateTime(iso: string): string {
  return format(parseLocal(iso), 'dd/MM/yyyy HH:mm')
}

/** "há 5 minutos", "em 3 dias". */
export function formatRelative(iso: string): string {
  return formatDistanceToNow(parseLocal(iso), { locale: ptBR, addSuffix: true })
}

/** `yyyy-MM` → "Setembro de 2026" (só a inicial em maiúscula). */
export function monthTitle(yyyyMm: string): string {
  const raw = format(parseLocal(`${yyyyMm}-01`), "MMMM 'de' yyyy", { locale: ptBR })
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

/** `yyyy-MM` → "set/26" — para KPIs e cabeçalhos curtos. */
export function monthShort(yyyyMm: string): string {
  return format(parseLocal(`${yyyyMm}-01`), 'MMM/yy', { locale: ptBR }).replace('.', '')
}

/** Segunda-feira da semana da data (a semana do departamento começa na segunda). */
export function startOfWeekMonday(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 })
}

/** Os `count` meses terminando no mês de `base`, do mais antigo ao mais novo. */
export function lastMonths(count: number, base: Date = new Date()): string[] {
  const months: string[] = []
  for (let i = count - 1; i >= 0; i -= 1) {
    months.push(toLocalMonth(new Date(base.getFullYear(), base.getMonth() - i, 1)))
  }
  return months
}

/**
 * Quanto tempo algo está esperando, na unidade que se lê de relance:
 * "40min", "5h", "2d 4h", "12d". Até 3 dias as horas ainda contam (um PR de
 * 2d 23h e um de 2d 1h pedem urgências diferentes); daí em diante são só dias.
 * Negativo (relógio adiantado) vira "0min" em vez de "-3min".
 */
export function formatEspera(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000))
  if (minutes < 60) return `${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  const rest = hours % 24
  return days < 3 && rest > 0 ? `${days}d ${rest}h` : `${days}d`
}
